import * as React from 'react'
import {
  CalendarBlank,
  DotsThreeVertical,
  Infinity as InfinityIcon,
  PencilSimple,
} from '@phosphor-icons/react'
import { CheckinCycle } from './types'
import { StatusLabel } from './status-label'
import { cycleStatus, formatDateRange, recurrenceLabel } from './utils'

type CycleListItemProps = {
  cycle: CheckinCycle
  onEdit?: (cycle: CheckinCycle) => void
  onUseAsTemplate?: (cycle: CheckinCycle) => void
  onRemove?: (cycle: CheckinCycle) => void
}

export const CycleListItem = ({
  cycle,
  onEdit,
  onUseAsTemplate,
  onRemove,
}: CycleListItemProps) => {
  const dateRange = formatDateRange(cycle)
  const [menuOpen, setMenuOpen] = React.useState(false)
  const menuRef = React.useRef<HTMLDivElement>(null)

  // One-time cycles read as a single calendar event; recurring cycles use the
  // infinity glyph to signal they repeat.
  const RecurrenceIcon =
    cycle.recurrence === 'one_time' ? CalendarBlank : InfinityIcon

  // Archived cycles expose a context menu (Use as template / Remove); active
  // cycles keep the inline edit pencil.
  const showMenu = cycle.archived

  React.useEffect(() => {
    if (!menuOpen) return undefined

    const onDocClick = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false)
      }
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false)
    }

    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [menuOpen])

  return (
    <li className="flex items-center justify-between gap-4 rounded-xl border border-[#E5E7EB] bg-white p-4">
      <div className="flex min-w-0 items-center gap-4">
        <RecurrenceIcon
          className="shrink-0 text-gray-400"
          size={22}
          weight="regular"
        />

        <div className="flex min-w-0 flex-col gap-1">
          <p className="m-0 truncate font-talent text-[16px] font-medium leading-[20px] text-[#1D264A]">
            {cycle.name}
          </p>
          <p className="m-0 truncate font-talent text-[14px] font-normal leading-[16px] tracking-[0.3px] text-[#7B8197]">
            {dateRange}
            {dateRange && ' · '}
            {recurrenceLabel(cycle.recurrence)}
          </p>
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-4">
        {!cycle.archived && <StatusLabel status={cycleStatus(cycle)} />}

        {showMenu ? (
          <div className="relative" ref={menuRef}>
            <button
              type="button"
              aria-label={`Actions for ${cycle.name}`}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((open) => !open)}
              className="flex h-8 w-8 items-center justify-center rounded text-gray-400 hover:bg-gray-50 hover:text-gray-600"
            >
              <DotsThreeVertical size={22} weight="bold" />
            </button>

            {menuOpen && (
              <div
                role="menu"
                className="absolute right-0 top-full z-10 mt-1 w-[200px] overflow-hidden rounded-xl border border-[#E5E7EB] bg-white py-1 shadow-[0_12px_32px_rgba(29,38,74,0.16)]"
              >
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false)
                    onUseAsTemplate?.(cycle)
                  }}
                  className="block w-full px-4 py-2.5 text-left font-talent text-[16px] font-normal leading-[20px] text-[#1D264A] hover:bg-[#F7F8FB]"
                >
                  Use as template
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false)
                    onRemove?.(cycle)
                  }}
                  className="block w-full px-4 py-2.5 text-left font-talent text-[16px] font-normal leading-[20px] text-[#E42309] hover:bg-[#FDF1EF]"
                >
                  Remove
                </button>
              </div>
            )}
          </div>
        ) : (
          <button
            type="button"
            aria-label={`Edit ${cycle.name}`}
            onClick={() => onEdit?.(cycle)}
            className="flex h-8 w-8 items-center justify-center rounded text-gray-400 hover:bg-gray-50 hover:text-gray-600"
          >
            <PencilSimple size={20} />
          </button>
        )}
      </div>
    </li>
  )
}
